import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import type { CodeDefinition, FacilityContact } from '../../../../js/api';
import { RoleEnum, HealthcareEntitiesService } from '../../../../js/api';
import { handleApiError } from '../../../../js/app/utils/errorhandling';
import { Button } from '../../components/button';
import { Dialog, DialogTitle, DialogBody, DialogActions } from '../../components/dialog';
import { useCodeDefinitionsByCategories } from '../../hooks/useCodeDefinitions';
import { useHasPermission } from '../../hooks/useHasPermission';
import { AddFacilityContactDialog } from './AddFacilityContactDialog';
import { EditFacilityContactDialog } from './EditFacilityContactDialog';
import { FACILITY_CONTACTS_QUERY_KEY } from './Facilities/queryKeys';
import { FacilityContactCard } from './FacilityContactCard';

function toRoleEnum(val: unknown): RoleEnum | undefined {
  return typeof val === 'string' && (Object.values(RoleEnum) as string[]).includes(val)
    ? (val as RoleEnum)
    : undefined;
}

type FacilityContactsBlockProps = {
  facilityId: string;
  facilityName?: string;
  /** Org id of the facility — used to scope the available-contacts list when staff
   *  is acting-as a different org than this facility belongs to. */
  healthcareEntityId?: string;
  canEdit: boolean;
  roleMap: Record<string, string> | undefined;
};

/**
 * Facility contacts list with add / edit / remove; uses shared contacts query key.
 */
export function FacilityContactsBlock({
  facilityId,
  facilityName,
  healthcareEntityId,
  canEdit,
  roleMap,
}: FacilityContactsBlockProps) {
  const queryClient = useQueryClient();
  const canDeleteContact = useHasPermission('delete__healthcare_entities__facilitycontact');
  const [isAddContactOpen, setIsAddContactOpen] = useState(false);
  const [isEditContactOpen, setIsEditContactOpen] = useState(false);
  const [editingContact, setEditingContact] = useState<FacilityContact | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [deletingContact, setDeletingContact] = useState<FacilityContact | null>(null);

  const { data: contactsData } = useQuery({
    queryKey: [FACILITY_CONTACTS_QUERY_KEY, facilityId],
    queryFn: () =>
      HealthcareEntitiesService.healthcareEntitiesFacilityContactsList({ facility: facilityId }),
    enabled: Boolean(facilityId),
  });

  const { data: codeDefinitionsByCategory } = useCodeDefinitionsByCategories([
    'FacilityContactRoles',
  ]);

  const contactRoleOptions: CodeDefinition[] = useMemo(
    () => codeDefinitionsByCategory?.FacilityContactRoles || [],
    [codeDefinitionsByCategory]
  );

  const contacts = (contactsData?.results ?? []) as FacilityContact[];

  const updateContactMutation = useMutation({
    mutationFn: (data: {
      id: string;
      contact: FacilityContact['contact'];
      role?: RoleEnum;
      receive_notifications?: boolean;
    }) =>
      HealthcareEntitiesService.healthcareEntitiesFacilityContactsPartialUpdate({
        id: data.id,
        requestBody: {
          contact: {
            ...data.contact,
            id: data.contact.id,
            email: data.contact.email,
          },
          role: data.role,
          receive_notifications: data.receive_notifications,
        },
      }),
    onSuccess: () => {
      toast.success('Contact updated successfully!');
      queryClient.invalidateQueries({ queryKey: [FACILITY_CONTACTS_QUERY_KEY, facilityId] });
      setIsEditContactOpen(false);
      setEditingContact(null);
    },
    onError: (error: unknown) => {
      handleApiError(error, { messagePrefix: 'Failed to update contact' });
    },
  });

  const deleteContactMutation = useMutation({
    mutationFn: (id: string) =>
      HealthcareEntitiesService.healthcareEntitiesFacilityContactsDestroy({ id }),
    onSuccess: () => {
      toast.success('Contact deleted successfully!');
      queryClient.invalidateQueries({ queryKey: [FACILITY_CONTACTS_QUERY_KEY, facilityId] });
      setIsDeleteDialogOpen(false);
      setDeletingContact(null);
    },
    onError: (error: unknown) => {
      handleApiError(error, { messagePrefix: 'Failed to delete contact' });
      setIsDeleteDialogOpen(false);
      setDeletingContact(null);
    },
  });

  return (
    <>
      <section className="tw:pb-8">
        <div className="tw:flex tw:justify-between tw:items-center tw:mb-4">
          <div className="tw:flex tw:items-center tw:gap-2">
            <i className="fa-solid fa-user-group tw:text-brand-green-0 tw:dark:text-brand-green-1" />
            <div className="tw:!mb-0 tw:dark:!text-brand-green-1 tw:text-xl tw:font-medium">
              Contacts
            </div>
          </div>
          {canEdit && (
            <Button color="brand-green-outline" onClick={() => setIsAddContactOpen(true)}>
              <i className="fa-solid fa-plus" />
              Add Contact
            </Button>
          )}
        </div>
        <div className="tw:grid tw:grid-cols-1 tw:gap-4">
          {contacts.length > 0 ? (
            contacts.map((facilityContact) => (
              <FacilityContactCard
                key={facilityContact.id}
                facilityContact={facilityContact}
                onEdit={
                  canEdit
                    ? (fc) => {
                        setEditingContact(fc as FacilityContact);
                        setIsEditContactOpen(true);
                      }
                    : undefined
                }
                onDelete={
                  canDeleteContact
                    ? (fc) => {
                        setDeletingContact(fc as FacilityContact);
                        setIsDeleteDialogOpen(true);
                      }
                    : undefined
                }
                canEdit={canEdit}
                roleMap={roleMap}
              />
            ))
          ) : (
            <div className="tw:text-zinc-500 tw:dark:text-zinc-400">No contacts yet.</div>
          )}
        </div>
      </section>

      <AddFacilityContactDialog
        facilityId={facilityId}
        healthcareEntityId={healthcareEntityId}
        open={isAddContactOpen}
        onClose={() => setIsAddContactOpen(false)}
        roleOptions={contactRoleOptions}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: [FACILITY_CONTACTS_QUERY_KEY, facilityId] });
          setIsAddContactOpen(false);
        }}
      />

      <EditFacilityContactDialog
        facilityName={facilityName}
        open={isEditContactOpen}
        onClose={() => {
          setIsEditContactOpen(false);
          setEditingContact(null);
        }}
        onDelete={
          canDeleteContact && editingContact
            ? () => {
                setDeletingContact(editingContact);
                setIsEditContactOpen(false);
                setIsDeleteDialogOpen(true);
              }
            : undefined
        }
        onSave={(updated) => {
          if (!editingContact) return;
          updateContactMutation.mutate({
            id: editingContact.id,
            contact: {
              ...editingContact.contact,
              ...updated,
              id: editingContact.contact.id,
              email: editingContact.contact.email,
            },
            role: updated.role,
            receive_notifications: updated.receive_notifications,
          });
        }}
        roleOptions={contactRoleOptions}
        contact={
          editingContact
            ? {
                ...editingContact.contact,
                role: toRoleEnum(editingContact.role),
                receive_notifications: editingContact.receive_notifications,
              }
            : undefined
        }
      />

      <Dialog
        open={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        className="tw:p-6"
      >
        <DialogTitle className="tw:dark:!text-white/90 tw:border-b tw:border-zinc-200">
          Remove Facility Contact
        </DialogTitle>
        <DialogBody>
          <p className="tw:text-zinc-900 tw:dark:text-zinc-100">
            Are you sure you want to remove this contact?{' '}
            <div className="tw:text-xs  tw:p-2 tw:mt-1 tw:bg-zinc-200 tw:text-zinc-600 tw:rounded-md">
              <i className="far fa-info-circle tw:mr-1 tw:text-zinc-500" />
              This does not delete the person from the organization&apos;s contact list or other
              facilities, just from this facility in this role.
            </div>
          </p>
          <div className="tw:text-zinc-900 tw:dark:text-zinc-100">
            {deletingContact?.contact?.first_name} {deletingContact?.contact?.last_name}
            {deletingContact?.role && (
              <div className="tw-text-xs tw:text-zinc-500">
                {roleMap?.[deletingContact.role] || deletingContact.role}
              </div>
            )}
            {deletingContact?.contact?.email && (
              <div className="tw-text-xs tw:text-zinc-500">{deletingContact.contact.email}</div>
            )}
          </div>
        </DialogBody>
        <DialogActions>
          <Button type="button" plain onClick={() => setIsDeleteDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            type="button"
            plain
            className="tw:bg-stoplight-danger tw:!text-white tw:hover:!bg-stoplight-danger/90"
            onClick={() => {
              if (deletingContact) {
                deleteContactMutation.mutate(deletingContact.id);
              }
            }}
            disabled={deleteContactMutation.isLoading}
          >
            {deleteContactMutation.isLoading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
